import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Input } from '@monigo/ui';
import { LoginSchema, type LoginInput } from '@monigo/shared';
import { supabase } from '../../lib/supabase.js';
import { AuthLayout } from '../../components/Layout/AuthLayout.js';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({ resolver: zodResolver(LoginSchema) });

  return (
    <AuthLayout title={t('auth.valueProposition')} subtitle={t('auth.valuePropositionSubtitle')}>
      <div className="space-y-5">
        <div className="space-y-2 text-center md:text-left">
          <h1 className="text-4xl font-display font-extrabold tracking-tight text-text">
            {t('auth.loginTitle')}
          </h1>
          <p className="text-base leading-7 text-text-muted">{t('auth.loginSubtitle')}</p>
        </div>

        <Card className="p-5 md:p-6">
          <form
            className="flex flex-col gap-4"
            onSubmit={handleSubmit(async (values) => {
              setSubmitError(null);
              const { error } = await supabase.auth.signInWithPassword(values);
              if (error) {
                setSubmitError(t('auth.errors.invalid'));
                return;
              }
              const from = (location.state as { from?: string } | null)?.from ?? '/app';
              navigate(from, { replace: true });
            })}
          >
            <Field label={t('auth.email')} error={errors.email?.message}>
              <Input
                type="email"
                placeholder="user@example.com"
                autoComplete="email"
                hasError={!!errors.email}
                {...register('email')}
              />
            </Field>
            <Field label={t('auth.password')} error={errors.password?.message}>
              <Input
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
                hasError={!!errors.password}
                {...register('password')}
              />
            </Field>

            <div className="flex justify-end gap-3 text-sm">
              <Link to="/forgot-password" className="font-semibold text-primary">
                {t('auth.forgotLink')}
              </Link>
            </div>

            {submitError ? (
              <p className="rounded-2xl bg-danger/10 p-3 text-sm text-danger">{submitError}</p>
            ) : null}

            <Button type="submit" isLoading={isSubmitting} className="w-full gap-2">
              {t('auth.loginCta')}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" />
                <path d="M12 5l7 7-7 7" />
              </svg>
            </Button>
          </form>
        </Card>

        <p className="text-center text-sm text-text-muted">
          {t('auth.noAccount')}{' '}
          <Link to="/signup" className="font-semibold text-primary">
            {t('auth.signupCta')}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label className="flex flex-col gap-2 text-sm">
      <span className="font-semibold text-text">{label}</span>
      {children}
      {error ? <span className="text-xs text-danger">{error}</span> : null}
    </label>
  );
}
